<template>
  <div>
    <h2 class="text-center mb-6 text-h3 font-weight-thin">
      Choose your plan
    </h2>
    <div class="toggle-container">
      <h4>Monthly</h4>
      <Toggle @checked="handleCheckbox" />
      <h4>Yearly</h4>
    </div>

    <div class="tile-container">
      <div class="price-tile" @click="handleFreePlanClick">
        <h3><v-icon color="green">mdi-check</v-icon> Free Plan</h3>
        <h2>
          £0
          <small class="text-h5 font-weight-thin">/ no cost</small>
        </h2>
        Suitable for individuals at no cost.
        <hr />
        Unlimited Personal Bookmarks
        <hr />
        Workspace Groups (Up to {{ config.groupsAllowed }} Group)
        <hr />
        Workspace Users (Unlimited)
        <hr />
        Create promo page
        <hr />
        Download our browser extension
        <hr />
        <button class="button" @click.stop="handleFreePlanClick">
          Get Started (Free)
        </button>
      </div>

      <div class="price-tile" @click="openLink('/subscriptions/starter')">
        <h3>Starter Plan <v-icon class="ml-1">mdi-star</v-icon></h3>
        <div class="most-popular-label">Most Popular</div>
        <h2>
          <small v-if="!monthlyActive">
            <span class="original-price"
              >£{{ calculateOriginalAnnualPrice(starterMonthly) }}</span
            >
          </small>
          £{{ monthlyActive ? starterMonthly : discountedStarterAnnual }}
          <small class="text-h5 font-weight-thin"
            >/ per {{ monthlyActive ? 'month' : 'year' }}</small
          >
        </h2>
        <template v-if="!monthlyActive">
          <div class="save-label">SAVE {{ discount * 100 }}%</div>
        </template>
        Perfect for individuals who want AI recommendations with their own
        OpenRouter key.
        <hr />
        <p><v-icon color="green">mdi-check</v-icon>Free Plan (included)</p>
        <hr />
        Workspace Groups (Up to {{ config.starterGroups }} Groups)
        <hr />
        Workspace Users (Unlimited)
        <hr />
        RSS Feeds
        <hr />
        <span
          ><v-icon color="black">mdi-creation-outline</v-icon> OpenRouter BYOK
          for AI features</span
        >
        <hr />
        <button class="button" @click.stop="openLink('/subscriptions/starter')">
          Get Started
        </button>
      </div>

      <div class="price-tile" @click="openLink('/subscriptions/pro')">
        <h3>Pro Plan <v-icon class="ml-1">mdi-crown</v-icon></h3>
        <h2>
          <small v-if="!monthlyActive">
            <span class="original-price"
              >£{{ calculateOriginalAnnualPrice(profMonthly) }}</span
            >
          </small>
          £{{ monthlyActive ? profMonthly : discountedProfAnnual }}
          <small class="text-h5 font-weight-thin"
            >/ per {{ monthlyActive ? 'month' : 'year' }}</small
          >
        </h2>
        <template v-if="!monthlyActive">
          <div class="save-label">SAVE {{ discount * 100 }}%</div>
        </template>
        A plan for a small professional team.
        <hr />
        <p><v-icon color="green">mdi-check</v-icon>Free Plan (included)</p>
        <hr />
        Workspace Groups (Up to {{ config.proGroups }} Groups)
        <hr />
        Workspace Users (Unlimited)
        <hr />
        Unlimited RSS Feeds
        <hr />
        <span
          ><v-icon color="black">mdi-creation-outline</v-icon> OpenRouter BYOK
          for AI features</span
        >
        <hr />
        <button class="button" @click.stop="openLink('/subscriptions/pro')">
          Get Started
        </button>
      </div>

      <div class="price-tile" @click="openLink('/subscriptions/business')">
        <h3>Business Plan <v-icon class="ml-1">mdi-crown</v-icon></h3>
        <h2>
          <small v-if="!monthlyActive">
            <span class="original-price"
              >£{{ calculateOriginalAnnualPrice(businessMonthly) }}</span
            >
          </small>
          £{{ monthlyActive ? businessMonthly : discountedBusinessAnnual }}
          <small class="text-h5 font-weight-thin"
            >/ per {{ monthlyActive ? 'month' : 'year' }}</small
          >
        </h2>
        <template v-if="!monthlyActive">
          <div class="save-label">SAVE {{ discount * 100 }}%</div>
        </template>
        A plan for a rapidly growing business.
        <hr />
        <p><v-icon color="green">mdi-check</v-icon>Free Plan (included)</p>
        <hr />
        Workspace Groups (Up to {{ config.businessGroups }} Groups)
        <hr />
        Workspace Users (Unlimited)
        <hr />
        Unlimited RSS Feeds
        <hr />
        <span
          ><v-icon color="black">mdi-creation-outline</v-icon> OpenRouter BYOK
          for AI features</span
        >
        <hr />
        <button
          class="button"
          @click.stop="openLink('/subscriptions/business')"
        >
          Get Started
        </button>
      </div>

      <div class="price-tile" @click="openLink('/subscriptions/enterprise')">
        <h3>Enterprise Plan <v-icon class="ml-1">mdi-crown</v-icon></h3>
        <h2>
          <small v-if="!monthlyActive">
            <span class="original-price"
              >£{{ calculateOriginalAnnualPrice(enterpriseMonthly) }}</span
            >
          </small>
          £{{ monthlyActive ? enterpriseMonthly : discountedEnterpriseAnnual }}
          <small class="text-h5 font-weight-thin"
            >/ per {{ monthlyActive ? 'month' : 'year' }}</small
          >
        </h2>
        <template v-if="!monthlyActive">
          <div class="save-label">SAVE {{ discount * 100 }}%</div>
        </template>
        A plan for a large company.
        <hr />
        <p><v-icon color="green">mdi-check</v-icon>Free Plan (included)</p>
        <hr />
        Workspace Groups (Unlimited)
        <hr />
        Workspace Users (Unlimited)
        <hr />
        Unlimited RSS Feeds
        <hr />
        <span
          ><v-icon color="black">mdi-creation-outline</v-icon> OpenRouter BYOK
          for AI features</span
        >
        <hr />
        <button
          class="button"
          @click.stop="openLink('/subscriptions/enterprise')"
        >
          Get Started
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable no-unused-vars, no-undef */
import { ref, computed, getCurrentInstance, onMounted, watch } from 'vue'
import Toggle from '../../buttons/Toggle.vue'

// Authentication state - reactive for both Vue 2 and Nuxt 3
const isLoggedIn = ref(false)

// Initialize auth based on context
const instance = getCurrentInstance()
const vuexStore =
  instance?.appContext?.config?.globalProperties?.$store ||
  instance?.proxy?.$store

// Setup authentication tracking
onMounted(async () => {
  if (vuexStore) {
    // Vue 2: Use Vuex store
    isLoggedIn.value = vuexStore.getters.isLoggedIn || false

    // Watch Vuex store changes
    if (instance?.proxy?.$watch) {
      instance.proxy.$watch(
        () => vuexStore.getters.isLoggedIn,
        newValue => {
          isLoggedIn.value = newValue
        }
      )
    }
  } else {
    // Nuxt 3: Use useAuth composable
    try {
      // Dynamic import to avoid webpack resolution in Vue 2 build
      const importPath = '../../nuxt-frontend/composables/useAuth.js'
      const useAuthModule = await new Function(
        `return import('${importPath}')`
      )()
      const { useAuth } = useAuthModule
      const authState = useAuth()

      // Sync auth state
      isLoggedIn.value = authState.isLoggedIn.value
      watch(authState.isLoggedIn, newValue => {
        isLoggedIn.value = newValue
      })
    } catch (error) {
      // Expected in Vue 2 build - composable doesn't exist
    }
  }
})

const props = defineProps({
  config: {
    type: Object,
    required: false,
    default: () => ({
      usersAllowed: 5,
      groupsAllowed: 1,
      starterGroups: 5,
      proGroups: 10,
      businessGroups: 20,
      discount: 0.1
    }),
    validator: value => {
      return (
        typeof value.usersAllowed === 'number' &&
        typeof value.groupsAllowed === 'number' &&
        typeof value.starterGroups === 'number' &&
        typeof value.proGroups === 'number' &&
        typeof value.businessGroups === 'number' &&
        typeof value.discount === 'number'
      )
    }
  }
})

// Pricing state
const monthlyActive = ref(true)
const starterMonthly = '1.99'
const profMonthly = '4.99'
const businessMonthly = '9.99'
const enterpriseMonthly = '19.99'

// Computed pricing values
const discount = computed(() => props.config.discount || 0.1)

const calculateAnnualPrice = monthly => 12 * Number(monthly)
const calculateDiscountedAnnual = monthly =>
  Math.ceil(calculateAnnualPrice(monthly) * (1 - discount.value))

const discountedStarterAnnual = computed(() =>
  calculateDiscountedAnnual(starterMonthly)
)
const discountedProfAnnual = computed(() =>
  calculateDiscountedAnnual(profMonthly)
)
const discountedBusinessAnnual = computed(() =>
  calculateDiscountedAnnual(businessMonthly)
)
const discountedEnterpriseAnnual = computed(() =>
  calculateDiscountedAnnual(enterpriseMonthly)
)

// Event handlers
const handleFreePlanClick = () => {
  const url = isLoggedIn.value ? '/dashboard' : '/signup'
  openLink(url)
}

const handleCheckbox = () => {
  monthlyActive.value = !monthlyActive.value
}

const openLink = (url, target = '_self') => {
  if (typeof window !== 'undefined') {
    window.open(url, target)
  }
}

// Helper functions
const calculateOriginalAnnualPrice = monthlyCost => 12 * Number(monthlyCost)

const getStarterRequests = () => 80
const getProRequests = () => 300
const getBusinessRequests = () => 800
const getEnterpriseRequests = () => 1500
</script>

<style lang="scss" scoped>
$desktop: 1251px;

.toggle-container {
  display: flex;
  align-items: center;
  flex-direction: row;
  justify-content: space-between;
  max-width: 220px;
  margin: 0 auto;
}

.tile-container {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  margin: 50px auto;
  padding: 0 30px;

  @media only screen and (max-width: 767px) {
    padding: 0;
  }

  @media only screen and (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media only screen and (min-width: $desktop) {
    grid-template-columns: repeat(5, 1fr);
  }
}

.price-tile {
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  position: relative;
  background-color: white;
  z-index: 1;
  padding: 20px;
  margin: 0;
  width: 100%;
  border-radius: 10px;
  align-items: center;
  border: solid 2px #e2e2e9;

  p {
    margin-top: 10px;
    margin-bottom: 10px;
    text-align: left;
  }

  @media only screen and (min-width: $desktop) {
    height: 530px;

    &:nth-of-type(4) {
      margin: 0;
    }
  }

  &:hover,
  &:active {
    cursor: pointer;
    transition: background 1s ease;
    background: linear-gradient(to right, #e65c3a, #faa767);
    color: white;

    :deep(.mdi-check),
    :deep(.mdi-creation-outline) {
      color: white !important;
    }
    border: solid 2px #faa767;
    .button {
      color: hsl(233, 13%, 49%);
      background: white;
      border-radius: 5px;
    }

    .original-price {
      color: white !important;
    }
  }

  hr {
    margin: 10px auto;
    border-top: 1px solid #e2e2e9;
    width: 90%;

    &:last-of-type {
      margin-bottom: 20px;
    }

    @media only screen and (min-width: $desktop) {
      margin: 0;
    }
  }

  .button {
    width: 90%;
    height: 40px;
    border-radius: 5px;
    background: linear-gradient(
      to right,
      hsl(210, 83%, 65%),
      rgb(25, 118, 210)
    );
    color: white;
    font-weight: bold;
    letter-spacing: 0.065em;
    border: 0;
    border-width: 0;
    margin: 0 auto;
    cursor: pointer;
  }

  .save-label {
    position: absolute;
    top: -10px;
    left: 50%;
    transform: translateX(-50%);
    background: #ff5252;
    color: white;
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 0.875rem;
    font-weight: bold;
  }

  .small {
    font-size: 0.8em;
  }

  h2 {
    @media only screen and (min-width: 1251px) and (max-width: 1620px) {
      font-size: 1.2em;
    }
  }

  .most-popular-label {
    position: absolute;
    top: -10px;
    right: -8px;
    background: #ff9800;
    color: white;
    padding: 4px 12px;
    border-radius: 5px 0 0 5px;
    font-size: 0.875rem;
    font-weight: bold;
  }
}

.original-price {
  font-size: 0.8em;
  color: #b9b3b3;
  text-decoration: line-through;
}
</style>
